import {inject, Injectable} from "@angular/core"
import type {CmLogger} from "@cm/log"
import {CM_LOGGER_AUTH} from "@common/services/logging"
import {MatSnackBar} from "@angular/material/snack-bar"
import {ActivatedRouteSnapshot, Router, RouterStateSnapshot} from "@angular/router"
import {MembershipRole} from "@generated"
import {AuthService} from "@common/services/auth/auth.service"
import {NotificationsService} from "@common/services/notifications/notifications.service"
import {OrganizationsService} from "@common/services/organizations/organizations.service"
import {PermissionsService} from "@common/services/permissions/permissions.service"
import {getOrganizationQueryParams} from "@app/platform/config/navigation-config"
import {SiloService} from "@common/services/auth/silo.service"

@Injectable({providedIn: "root"})
export class AuthGuard {
    readonly notifications = inject(NotificationsService)
    private readonly logger: CmLogger = inject(CM_LOGGER_AUTH)
    private readonly auth = inject(AuthService)
    private readonly router = inject(Router)

    async canActivate(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
        const user = await this.auth.user
        if (user) {
            return true
        }

        // not logged in so redirect to login page with the return url and return false
        this.logger.info("Auth guard redirecting to login", state)
        return this.router.createUrlTree(["/login"], {queryParams: {returnUrl: state.url}})
    }
}

@Injectable({providedIn: "root"})
export class StaffGuard {
    readonly permission = inject(PermissionsService)
    $can = this.permission.$to
    private readonly auth = inject(AuthService)
    private readonly router = inject(Router)
    private readonly snackBar = inject(MatSnackBar)

    async canActivate() {
        const user = await this.auth.user
        if (!user?.isStaff) {
            this.snackBar.open("Permission denied.", "", {duration: 3000})
            return this.router.parseUrl("/login")
        }
        return true
    }
}

@Injectable({providedIn: "root"})
export class PicturesAuthGuard {
    readonly permission = inject(PermissionsService)
    readonly organizations = inject(OrganizationsService)
    private readonly logger: CmLogger = inject(CM_LOGGER_AUTH)
    $can = this.permission.$to
    private readonly auth = inject(AuthService)
    private readonly router = inject(Router)

    async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
        const user = await this.auth.user
        if (user) {
            return true
        }

        this.logger.debug("Pictures auth guard redirecting to login", user, route.queryParams)
        return this.router.createUrlTree(["/login"], {queryParams: {returnUrl: state.url}})
    }
}

@Injectable({providedIn: "root"})
export class OrganizationAdminGuard {
    readonly permission = inject(PermissionsService)
    $can = this.permission.$to
    private readonly auth = inject(AuthService)
    private readonly snackBar = inject(MatSnackBar)

    async canActivate() {
        const user = await this.auth.user
        // Self-service admins reach organization settings — that's where "Upgrade to download" routes and
        // the upgrade lives. Covers the staff preview (which emulates the auto-Admin owner) and a real
        // self-service Admin session, both determined from the active silo.
        if (this.auth.$isSelfServiceAdmin()) {
            return true
        }
        const activeOrganizationId = this.auth.silo.$silo()?.organization?.id
        if (user && activeOrganizationId) {
            const activeMembership = user.memberships.find((m) => m.organization.id === activeOrganizationId)
            if (activeMembership && activeMembership.role === MembershipRole.Admin) {
                return true
            }
        }

        this.snackBar.open("Permission denied.", "", {duration: 3000})
        return false
    }
}

@Injectable({providedIn: "root"})
export class SubscriptionGuard {
    private readonly auth = inject(AuthService)
    private readonly router = inject(Router)

    async canActivate() {
        const user = await this.auth.user
        if (!user) {
            return false
        }

        // Staff and superusers bypass subscription checks
        if (user.isStaff || user.isSuperuser) {
            return true
        }

        // Self-service signup users need to finish onboarding
        // selfServiceSignup=true with no onboardingCompletedAt means pending onboarding
        if (user.selfServiceSignup && !user.onboardingCompletedAt) {
            return this.router.createUrlTree(["/onboarding"])
        }

        // Self-service orgs without an active subscription (chose Free, trial elapsed, or subscription lapsed) are on the Free plan
        return true
    }
}

/**
 * Guard that redirects authenticated users away from public pages (login, sign-up).
 * Use this on routes that should only be accessible to unauthenticated users.
 */
@Injectable({providedIn: "root"})
export class RedirectIfAuthenticatedGuard {
    private readonly auth = inject(AuthService)
    private readonly router = inject(Router)
    private readonly silo = inject(SiloService)

    async canActivate() {
        const user = await this.auth.user
        if (user) {
            const orgParam = getOrganizationQueryParams(this.silo.$silo())
            // User is already logged in - redirect to pictures
            return this.router.createUrlTree(["pictures"], {
                queryParams: {
                    tab: orgParam ? "picture" : undefined,
                    orgParam,
                },
            })
        }
        // Not logged in - allow access to the public page
        return true
    }
}

@Injectable({providedIn: "root"})
export class OnboardingGuard {
    private readonly auth = inject(AuthService)
    private readonly router = inject(Router)

    async canActivate(route: ActivatedRouteSnapshot) {
        const user = await this.auth.user
        if (!user) {
            // Not logged in - redirect to login
            return this.router.createUrlTree(["/login"])
        }

        // Check if returning from Stripe checkout (session_id in query params)
        const sessionId = route.queryParams["session_id"]
        if (sessionId) {
            // Allow access to complete the onboarding flow after Stripe checkout
            return true
        }

        // Only self-service signup users who haven't completed onboarding should access this page
        // Legacy/invited users and completed users should be redirected
        if (!user.selfServiceSignup || user.onboardingCompletedAt) {
            return this.router.createUrlTree(["/pictures"])
        }

        // Self-service user who hasn't completed onboarding - allow access
        return true
    }
}
